/**
 * Krok ZAK1 bod 5 — PANEL „CO ZBYVA ZJISTIT" NA DETAILU ZAKAZKY.
 *
 * Po ZAK1 se zakazka plni sama z otevrenych dat (RUIAN, mapove vrstvy,
 * rejstrik) a makler potrebuje videt DRUHOU STRANU teze mince: co uz se
 * vyplnilo bez nej a co se bez cloveka nedozvime. Dokud tenhle rozpad
 * neexistoval, musel projit cely nabor bod po bodu, aby zjistil, ze mu chybi
 * tri veci z vyuctovani.
 *
 * ZDROJ PRAVDY O ZDROJI UDAJE JE SABLONA, ne vycet v tomhle souboru: kazdy
 * bod naboru nese `prefill` („odkud to umime") a `where` („kde to zjistit")
 * z katalogu kancelare. Panel je proto jen PREVOD tech dvou retezcu na
 * skupinu — novy bod katalogu se do panelu dostane sam.
 *
 * Cista funkce nad DTO, zadny dotaz na API (stejna konvence jako
 * `case-origin.ts`); hodnoty i stav naboru uz stranka ma.
 */

import type { ChecklistPointDto, ChecklistTemplateDto, IntakeContextDto } from '@reos/field-intake';
import { isAnswerable, isPointVisible, type PointAnswers } from './intake-checklist.js';

/** Skupiny panelu v poradi, v jakem je makler resi. */
export const REMAINING_SOURCES = ['survey', 'owner', 'hoa', 'penb', 'lv', 'billing', 'other'] as const;
export type RemainingSource = (typeof REMAINING_SOURCES)[number];

export interface RemainingGroup {
  readonly source: RemainingSource;
  readonly count: number;
  /** Body skupiny v poradi sablony — proklik vede na prvni z nich. */
  readonly points: readonly ChecklistPointDto[];
}

export interface CaseRemaining {
  /** Kolik poli uz vyplnila otevrena data (registr), bez zasahu cloveka. */
  readonly filled: number;
  readonly remaining: number;
  readonly groups: readonly RemainingGroup[];
}

/**
 * Prevod `where` bodu na skupinu panelu. `where` je VYCET zdroju oddeleny
 * lomitkem („RÚIAN/majitel/štítek kotle"); rozhoduje PRVNI zdroj, ktery
 * panel zna, protoze katalog je radi od nejspolehlivejsiho. Zdroje, ktere uz
 * umi otevrena data (RÚIAN), se preskakuji — pole, ktere zbyva, zbyva na
 * cloveku.
 */
const SOURCE_PATTERNS: readonly (readonly [RegExp, RemainingSource])[] = [
  [/prohlídk|měření|foto|půdorys|doklad totožnosti|půd[aě]|předání/i, 'survey'],
  /*
   * „Předpis záloh" je doklad SVJ, ne vyuctovani — bez teto hranice by
   * mesicni zalohy spadly k vyuctovani a makler by je hledal u dodavatele.
   */
  [/SVJ|družstv|předpis záloh/i, 'hoa'],
  [/vyúčtování/i, 'billing'],
  [/PENB/i, 'penb'],
  [/LV|KN|insolven|katastr/i, 'lv'],
  [/majitel|prohlášení|smlouv|plná moc|OP|štítek/i, 'owner'],
  /* Co nedodá ani jeden z nich: text inzeratu, exportni profil, cena. */
  [/makléř|exportní profil|AI/i, 'other'],
];

export function remainingSourceOf(point: ChecklistPointDto): RemainingSource {
  for (const part of point.where.split('/')) {
    const trimmed = part.trim();
    if (trimmed === '' || /^RÚIAN$/i.test(trimmed)) continue;
    for (const [pattern, source] of SOURCE_PATTERNS) {
      if (pattern.test(trimmed)) return source;
    }
  }
  return 'other';
}

/** Fakta, ktera do zakazky dodal REGISTR (RUIAN, rejstrik) — tedy „samo". */
function filledByOpenData(context: IntakeContextDto): ReadonlySet<string> {
  return new Set(
    context.known
      .filter((fact) => fact.sourceType === 'registry')
      .map((fact) => fact.factKey),
  );
}

/**
 * Rozpad „vyplneno samo / zbyva" nad sablonou naboru.
 *
 * `answers` jsou odpovedi z naboru (vcetne offline fronty, viz
 * `intake-checklist.ts`), `covered` body, ktere REOS uz zna odjinud. Pocita se
 * nad PLNYM naborem (`mode: 'full'`), protoze panel rika, co zbyva CELKEM —
 * ne co zbyva v rychlem rezimu.
 */
export function caseRemaining(
  template: ChecklistTemplateDto | null,
  context: IntakeContextDto | null,
  answers: PointAnswers = new Map(),
  covered: ReadonlySet<string> = new Set(),
): CaseRemaining {
  if (template === null) return { filled: 0, remaining: 0, groups: [] };
  const open = context === null ? new Set<string>() : filledByOpenData(context);
  const known = new Set(context === null ? [] : context.known.map((fact) => fact.factKey));
  let filled = 0;
  const bySource = new Map<RemainingSource, ChecklistPointDto[]>();
  for (const point of template.points) {
    if (!isAnswerable(point) || !isPointVisible(point, answers)) continue;
    const factKey = point.factKey;
    if (factKey !== null && open.has(factKey)) {
      filled += 1;
      continue;
    }
    const answered = covered.has(point.id) || (answers.get(point.id) ?? null) !== null;
    if (answered || (factKey !== null && known.has(factKey))) continue;
    const source = remainingSourceOf(point);
    const bucket = bySource.get(source);
    if (bucket === undefined) bySource.set(source, [point]);
    else bucket.push(point);
  }
  const groups = REMAINING_SOURCES.flatMap((source) => {
    const points = bySource.get(source);
    return points === undefined || points.length === 0
      ? []
      : [{ source, count: points.length, points } satisfies RemainingGroup];
  });
  return { filled, remaining: groups.reduce((sum, group) => sum + group.count, 0), groups };
}
